"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { ServiceCard } from "@/components/shared";
import { getImageUrl } from "@/utils/utils";

interface HomePageServicesClientProps {
  lang: string;
  services: any[];
  homepageContent: any;
}

export default function HomePageServicesClient({
  lang,
  services,
  homepageContent,
}: HomePageServicesClientProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection for optimized animations
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Animation variants - exact match from original
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? 30 : 60, // Reduced movement on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Faster animation on mobile
        ease: "easeOut" as const,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <motion.section
      className="py-12 sm:py-16 lg:py-20 xl:py-[100px] bg-white"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: isMobile ? 0.05 : 0.1 }}
      variants={sectionVariants}
    >
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header Container */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6 lg:gap-0 mb-12 sm:mb-16 lg:mb-20">
          <div className="flex flex-col items-start gap-3 sm:gap-4 max-w-full lg:max-w-[639px]">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 sm:w-[30px] h-[1px] bg-neutral-700"></div>
              <span className="text-dark-200 text-sm sm:text-[16px] font-medium leading-[16px] capitalize">
                {homepageContent?.services?.title || "Our Services"}
              </span>
            </div>
            <h2 className="text-dark-200 text-2xl sm:text-3xl lg:text-4xl xl:text-[48px] font-medium leading-tight xl:leading-[58px] tracking-tight xl:tracking-[-1.44px]">
              {homepageContent?.services?.subtitle ||
                "Expert spectrum of services to meet your needs"}
            </h2>
          </div>

          <a
            href={`/${lang}/services`}
            className="group flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-[30px] border border-primary-800 text-primary-800 hover:text-white hover:bg-[#1a5852]/90 transition-all duration-300 self-start lg:self-auto shadow-none hover:shadow-lg"
          >
            <svg
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transform group-hover:translate-x-1 transition-transform duration-300"
            >
              <path
                d="M6.91793 7.37499L2.0406 12.2107C1.8326 12.4188 1.49927 12.4188 1.3326 12.2107L1.0406 11.9186C0.991705 11.8736 0.952675 11.819 0.925976 11.7581C0.899278 11.6973 0.885492 11.6315 0.885492 11.5651C0.885492 11.4986 0.899278 11.4329 0.925976 11.372C0.952675 11.3112 0.991705 11.2566 1.0406 11.2116L5.29393 7.00013L1.0406 2.83138C0.991491 2.78639 0.952276 2.73167 0.925448 2.67069C0.89862 2.60972 0.884766 2.54383 0.884766 2.47721C0.884766 2.41058 0.89862 2.34469 0.925448 2.28372C0.952276 2.22274 0.991491 2.16802 1.0406 2.12303L1.33393 1.83088C1.5006 1.62278 1.83393 1.62278 2.04193 1.83088L6.9606 6.66663C7.04472 6.76805 7.08713 6.89769 7.0792 7.02925C7.07128 7.1608 7.01362 7.28441 6.91793 7.37499Z"
                fill="currentColor"
              />
            </svg>
            <span className="text-sm sm:text-[16px] font-medium leading-[16px] capitalize">
              {homepageContent?.services?.ctaText || "More Services"}
            </span>
          </a>
        </div>

        {/* Services Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 xl:gap-[58px]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          variants={sectionVariants}
        >
          {services.slice(0, 6).map((service, index) => (
            <ServiceCard
              key={service.slug || index}
              title={service.title}
              excerpt={service.excerpt || ""}
              number={(index + 1).toString()}
              image={getImageUrl(service.image?.url || "")}
              href={`/${lang}/services/${service.slug}`}
            />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
